import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import MeetingsAPI from "../../api/MeetingsAPI";
import CustomDatePicker from "../CustomDatePicker/CustomDatePicker";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import Switch from "../Switch/Switch";

export interface BlockedDate {
  id?: number;
  start: string;
  end: string;
}

type Props = {
  defaultDate?: Date;
  fn?: () => void;
  timeZone?: string;
};

const BlockedDatesForm: React.FC<Props> = ({ defaultDate, fn = () => null, timeZone }) => {
  const initializeDates = (date: Date) => {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const end = new Date(date);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  const initialDate = defaultDate || new Date();
  const { start: initialStart, end: initialEnd } = initializeDates(initialDate);
  
  const [start, setStart] = useState(initialStart);
  const [end, setEnd] = useState(initialEnd);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [blockAllDay, setBlockAllDay] = useState(true);
  const { t } = useTranslation();

  // Only adjust times when switching between all day and hourly modes
  const handleBlockTypeChange = (newBlockAllDay: boolean) => {
    if (newBlockAllDay) {
      let adjustedStart = new Date(start);
      adjustedStart.setHours(0, 0, 0, 0);
      let adjustedEnd = new Date(end);
      adjustedEnd.setHours(23, 59, 59, 999);

      setStart(adjustedStart);
      setEnd(adjustedEnd);
    } else {
      // When switching to hourly blocking, set start time to 9 AM
      let adjustedStart = new Date(start);
      adjustedStart.setHours(9, 0, 0, 0);
      let adjustedEnd = new Date(end);
      adjustedEnd.setHours(17, 0, 0, 0);

      setStart(adjustedStart);
      setEnd(adjustedEnd);
    }
    setBlockAllDay(newBlockAllDay);
  };

  // Handle date changes in full day mode
  const handleDateChange = (newDate: Date, isStart: boolean) => {
    if (blockAllDay) {
      const adjustedDate = new Date(newDate);
      if (isStart) {
        adjustedDate.setHours(0, 0, 0, 0);
        setStart(adjustedDate);
        
        // If start date is after end date, update end date to match start date
        const currentEnd = new Date(end);
        currentEnd.setHours(0, 0, 0, 0);
        if (adjustedDate > currentEnd) {
          const newEnd = new Date(adjustedDate);
          newEnd.setHours(23, 59, 59, 999);
          setEnd(newEnd);
        }
      } else {
        adjustedDate.setHours(23, 59, 59, 999);
        setEnd(adjustedDate);
      }
    } else {
      if (isStart) {
        setStart(newDate);
      } else {
        setEnd(newDate);
      }
    }
  };

  useEffect(() => {
    // For all day mode, we only need to check if the dates are valid
    if (blockAllDay) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      setIsButtonDisabled(endDate < startDate);
    } else {
      // For hourly mode, we need to check both date and time
      setIsButtonDisabled(end <= start);
    }
  }, [start, end, blockAllDay]);

  const blockDate = async () => {
    await MeetingsAPI.block({
      start: start.toISOString(),
      end: end.toISOString(),
    });
    fn();
  };

  return (
    <div className="flex flex-col">
      <div className="mt-0 mb-6 p-3 bg-blue-50 rounded-md border border-blue-100">
        <div className="flex items-start space-x-2">
          <div className="text-blue-500 mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <div>
            <p className="text-sm text-blue-700">
              {t("calendar.blocked_dates.form.info_note", {
                defaultValue: "Blocked periods will prevent bookings from being made during these times in your calendar. You can block full days or specific hours as needed (holidays, special events, or other reasons)."
              })}
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{t("calendar.blocked_dates.form.all_day")}</span>
            <Switch
              checked={blockAllDay}
              onChange={() => handleBlockTypeChange(!blockAllDay)}
            />
          </div>
        </div>

        <div className="flex space-x-5">
          <CustomDatePicker
            label="calendar.blocked_dates.form.start"
            value={start}
            onChange={(c) => handleDateChange(c, true)}
            showTimeSelect={!blockAllDay}
            dateFormat={blockAllDay ? "yyyy-MM-dd" : "yyyy-MM-dd HH:mm"}
            timeZone={timeZone}
          />
          <CustomDatePicker
            label="calendar.blocked_dates.form.end"
            value={end}
            onChange={(c) => handleDateChange(c, false)}
            showTimeSelect={!blockAllDay}
            dateFormat={blockAllDay ? "yyyy-MM-dd" : "yyyy-MM-dd HH:mm"}
            timeZone={timeZone}
          />
        </div>

        <div className="flex justify-center">
          <PrimaryButton 
            text={t("calendar.blocked_dates.form.confirm")!} 
            fn={blockDate} 
            isDisabled={isButtonDisabled}
          />
        </div>
      </div>
    </div>
  );
};

export default BlockedDatesForm;
